import { useEffect, useRef, useState } from 'react';
import { search } from '../lib/api';
import type { SearchResult } from '../lib/types';

type Props = {
  label: string;
  placeholder: string;
  value: string;
  type?: string;
  onChange: (value: string, item?: SearchResult) => void;
};

export function Autocomplete({ label, placeholder, value, onChange, type = 'cities' }: Props) {
  const [items, setItems] = useState<SearchResult[]>([]);
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let ignore = false;
    if (!value || value.length < 1) {
      setItems([]);
      return;
    }
    const t = setTimeout(async () => {
      try {
        const results = await search(value, type);
        if (!ignore) setItems(results.slice(0, 8));
      } catch {
        if (!ignore) setItems([]);
      }
    }, 120);
    return () => { ignore = true; clearTimeout(t); };
  }, [value, type]);

  useEffect(() => {
    function onDoc(e: MouseEvent) {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  return (
    <div className="relative" ref={ref}>
      <label className="mb-2 block font-mono text-[11px] uppercase tracking-[0.22em] text-charcoal/55">{label}</label>
      <input
        value={value}
        onChange={(e) => { onChange(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        placeholder={placeholder}
        className="focus-ring w-full rounded-3xl border border-charcoal/10 bg-warm px-5 py-4 text-lg shadow-soft placeholder:text-charcoal/28"
        autoComplete="off"
      />
      {open && items.length > 0 && (
        <div className="absolute z-20 mt-2 max-h-80 w-full overflow-auto rounded-3xl border border-charcoal/10 bg-warm p-2 shadow-soft">
          {items.map((item) => (
            <button
              key={`${item.type}-${item.id}`}
              type="button"
              onClick={() => { onChange(item.label, item); setOpen(false); }}
              className="focus-ring flex w-full flex-col rounded-2xl px-4 py-3 text-left hover:bg-bone"
            >
              <span className="text-sm font-semibold text-charcoal">{item.label}</span>
              {item.sublabel && <span className="text-xs text-charcoal/55">{item.sublabel}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
